import { Action, NativeSelect } from '../../shared/design/controls';
import { Button as DesignButton, Badge as DesignBadge } from "../../shared/design/controls";
export { Input, Textarea, Field } from "../../shared/design/controls";
// The dashboard's layout and data primitives.
//
// Colour and both modes come from the self-owned token sheet in
// styles/index.css. Nothing here names a palette value; everything
// reaches for a role (`accent`, `ok`, `danger`, `fg-muted`) so one
// definition serves light and dark alike.
//
// Two invariants this file exists to hold:
//   · a control's height is written once (`CONTROL_H`), never
//     inferred from padding;
//   · a card's contents sit at one inset (`px-5`), header and body
//     and table cells alike, so the left edge is a single line.

import { ChevronRight, Monitor, Moon, Sun } from 'lucide-react';
import { isValidElement, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { useT } from '../i18n';
import { setThemeMode, useThemeMode, type ThemeMode } from '../lib/theme';


// ── Card ───────────────────────────────────────────────────

export function Card({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded border border-border bg-surface ${className}`}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between border-b border-border px-5 py-4">
      <div>
        <h3 className="text-[15px] font-semibold tracking-tight text-fg">{title}</h3>
        {subtitle && (
          <p className="mt-0.5 text-xs text-fg-subtle">{subtitle}</p>
        )}
      </div>
      {action}
    </div>
  );
}

// ── Controls ───────────────────────────────────────────────

/**
 * The two control heights in the product. Everything that can sit in
 * a row with a button — buttons, inputs, selects — uses one of these
 * so the row has a single baseline. Before this the app had ten
 * different padding pairs standing in for a height, and no two
 * adjacent controls agreed.
 */
export const CONTROL_H = { sm: 'h-[var(--control-height-small)]', md: 'h-[var(--control-height)]' } as const;

/** Select at a shared control height. */
export function Select({
  size = 'md',
  ...props
}: // `size` on a native select is a row count nothing here sets, and
// keeping it would intersect with ours to `never`.
Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'> & { size?: 'sm' | 'md' }) {
  const { className = '', children, ...rest } = props;
  const metrics =
    size === 'sm' ? `${CONTROL_H.sm} pl-2 pr-6 text-xs` : `${CONTROL_H.md} pl-2 pr-7 text-sm`;
  return (
    <NativeSelect {...rest} className={`${metrics} ${className}`}>
      {children}
    </NativeSelect>
  );
}

// ── Button ─────────────────────────────────────────────────

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md';

/**
 * The single source of a button's appearance, shared by `<Button>`
 * and `<LinkButton>`.
 *
 * Height is fixed, never inferred from padding: derive it from
 * padding plus line-height and a button carrying an icon or a count
 * ends up a pixel taller than the plain one beside it, and a toolbar
 * of six never lines up. Padding sets width only.
 */
export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  icon = false,
): string {
  return `button button-${variant} ${size === 'sm' ? 'button-small' : ''} ${icon ? 'button-icon' : ''}`;
}

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  onClick,
  disabled,
  type = 'button',
  title,
  icon = false,
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  onClick?: () => void;
  disabled?: boolean;
  type?: 'button' | 'submit';
  /** Also the accessible name — an icon-only button has no text to
   *  read out, so this is the only thing a screen reader can announce. */
  title?: string;
  /** Square, for a lone glyph. */
  icon?: boolean;
}) {
  return (
    <DesignButton
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      variant={variant}
      size={size === 'sm' ? 'small' : 'regular'}
      className={icon ? 'button-icon' : ''}
    >
      {children}
    </DesignButton>
  );
}

/**
 * A link that is a button.
 *
 * `← All` was a hand-styled `<Link>` sitting in a row of `<Button>`s
 * and it stood 4px taller than every one of them, because the height
 * had been written twice and the two copies drifted. Anything that
 * looks like a button now derives its class from the same place.
 */
export function LinkButton({
  to,
  children,
  variant = 'secondary',
  size = 'md',
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  return (
    <Link to={to} className={buttonClass(variant, size)}>
      {children}
    </Link>
  );
}

// ── Badge ──────────────────────────────────────────────────

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info';
}) {
  return <DesignBadge tone={tone === 'ok' ? 'success' : tone === 'warn' ? 'warning' : tone}>{children}</DesignBadge>;
}

// ── PageShell ──────────────────────────────────────────────

/**
 * The anatomy every non-split page shares: a slim toolbar naming
 * the page (plus its controls), and a content region that scrolls
 * on its own. Content is left-aligned under a readable cap —
 * admin lists at full monitor width read as spreadsheets.
 */
export function PageShell({
  title,
  toolbar,
  children,
}: {
  title: ReactNode;
  toolbar?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full min-w-0 flex-col">
      <header className="flex h-11 shrink-0 items-center gap-3 border-b border-border bg-bg px-4">
        <h1 className="shrink-0 whitespace-nowrap text-sm font-semibold">{title}</h1>
        {toolbar}
      </header>
      {/* Full width: an app pane uses the monitor it was given.
          Density comes from the tables' own column discipline, not
          from a page-level width cap. */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────

/**
 * The app's unit of structure: a bordered surface with a named
 * header. Every region of a screen lives inside one — content
 * never floats on the canvas, and a panel with no data still
 * stands (its body shows a quiet empty line instead of the panel
 * vanishing and leaving a hole in the page).
 */
export function Panel({
  title,
  action,
  children,
  className = '',
  padded = false,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Give the body the standard inset. Evidence bodies (code,
   *  replay, tables) manage their own edges. */
  padded?: boolean;
}) {
  return (
    <section
      className={`flex min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-surface ${className}`}
    >
      <header className="flex h-9 shrink-0 items-center justify-between gap-3 border-b border-border px-3.5">
        <h3 className="truncate text-sm font-semibold text-fg-muted">{title}</h3>
        {action}
      </header>
      <div className={`min-h-0 flex-1 ${padded ? 'p-3.5' : ''}`}>{children}</div>
    </section>
  );
}

/** A quiet in-panel empty line — the panel stays, the void goes. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return <p className="px-3.5 py-4 text-sm text-fg-subtle">{children}</p>;
}

/** The public pages' shared frame: brand above, one card, canvas
 *  behind — the same visual family as the app the door leads into. */

/** A keycap, wherever a shortcut is named. One definition so ⌘K in
 *  the topbar and j/k in the empty state read as the same object. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-surface px-1 font-mono text-xs leading-4 text-fg-muted">
      {children}
    </kbd>
  );
}

/** A collapsed strip panel for the obligatory corners
 *  (occurrences, raw environment, activity). */
export function FoldPanel({
  title,
  defaultOpen = false,
  children,
}: {
  title: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-surface">
      <Action
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-9 w-full items-center gap-1.5 px-3.5 text-left text-sm font-semibold text-fg-muted hover:text-fg"
      >
        <ChevronRight
          aria-hidden
          className={`h-3.5 w-3.5 text-fg-subtle transition-transform ${open ? 'rotate-90' : ''}`}
        />
        {title}
      </Action>
      {open && <div className="border-t border-border">{children}</div>}
    </section>
  );
}

// ── DataTable ──────────────────────────────────────────────

/**
 * A cell's value, rendered as what it is.
 *
 * This used to be `String(value ?? '—')`, which turns anything that is
 * not a primitive into the literal text `[object Object]` — including
 * a React element, which is exactly what seven pages put in their rows
 * instead of writing a `render` callback for every column. The whole
 * projects table read `[object Object]` five times across.
 *
 * `String()` is the wrong tool because it never fails: it converts
 * everything, so a type mismatch shows up as plausible-looking text
 * rather than as an error anyone would notice.
 */
function cell(row: unknown, key: PropertyKey): ReactNode {
  const v = (row as Record<PropertyKey, unknown>)[key];
  if (v === null || v === undefined || v === '') return '—';
  if (isValidElement(v)) return v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export type Column<T> = {
  key: keyof T | string;
  label: string;
  render?: (row: T) => ReactNode;
  width?: string;
  /** Numeric columns read right-aligned, tabular. */
  align?: 'left' | 'right';
};

export function DataTable<T>({
  columns,
  rows,
  empty,
  rowKey,
  onRowClick,
  activeKey,
  footer,
  appearance = 'legacy',
}: {
  appearance?: 'legacy' | 'design';
  columns: Column<T>[];
  rows: T[];
  empty?: ReactNode;
  rowKey?: (row: T) => string;
  /** Rows become interactive (hover affordance, pointer). */
  onRowClick?: (row: T) => void;
  /** The selected row's key, filled like an open file. */
  activeKey?: null | string;
  /** Count line under the table — "37 of 37" furniture. */
  footer?: ReactNode;
}) {
  // Resolved here rather than as a default parameter: a default is
  // evaluated where the signature is written, which has no hook and
  // so no locale, and every caller that omitted `empty` was getting
  // the English string regardless of language.
  const t = useT();
  if (rows.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-fg-subtle">
        {empty ?? t('table.empty')}
      </div>
    );
  }
  const alignCls = (c: Column<T>) =>
    c.align === 'right' ? 'text-right tabular-nums' : 'text-left';
  return (
    <div className={appearance === 'design' ? 'data-wrap' : 'overflow-x-auto'}>
      <table className={appearance === 'design' ? 'data-table' : 'w-full text-sm'} data-layout="content">
        <thead className="border-b border-border bg-bg/50">
          <tr>
            {columns.map((c) => (
              <th
                key={String(c.key)}
                className={`whitespace-nowrap ${appearance === 'legacy' ? 'px-4 py-2 text-xs font-medium text-fg-muted' : ''} ${alignCls(c)}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {rows.map((r, i) => {
            const key = rowKey ? rowKey(r) : String(i);
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={clsx(
                  activeKey != null && key === activeKey
                    ? 'bg-raised'
                    : onRowClick
                      ? 'hover:bg-surface/60'
                      : 'hover:bg-surface/50',
                  onRowClick && 'cursor-pointer',
                )}
              >
                {columns.map((c) => (
                  <td
                    key={String(c.key)}
                    className={`whitespace-nowrap ${appearance === 'legacy' ? 'px-4 py-2 text-fg-muted' : ''} ${alignCls(c)}`}
                  >
                    {c.render ? c.render(r) : cell(r, c.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {footer && (
        <div className="border-t border-border px-4 py-1.5 text-right font-mono text-xs tabular-nums text-fg-subtle">
          {footer}
        </div>
      )}
    </div>
  );
}

// ── Page header ────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  action,
  actions,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-6 flex items-start justify-between">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{title}</h2>
        {subtitle && (
          <p className="mt-1 text-sm text-fg-subtle">{subtitle}</p>
        )}
      </div>
      {action ?? actions}
    </div>
  );
}

// ── Empty state ────────────────────────────────────────────

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <div className="rounded border border-border bg-surface p-12 text-center">
      <p className="text-fg-muted">{title}</p>
      {hint && <p className="mt-2 text-sm text-fg-subtle">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ── Error banner ───────────────────────────────────────────

/**
 * A failure, and what the server said about it.
 *
 * `reason` is the second half and it is not optional in spirit: a
 * banner reading only "could not load" cannot separate a session that
 * expired (log in again) from a project you cannot see (ask someone)
 * from a database that is down (wait). Six of the read paths dropped
 * it, so `formatApiError` — whose whole job is to name the status and
 * the message — was being computed and thrown away on most screens.
 *
 * Kept visually quieter than the sentence: an operator reads the
 * sentence first and the code when the sentence is not enough.
 */
export function ErrorBanner({
  children,
  reason,
}: {
  children: ReactNode;
  reason?: string | null;
}) {
  return (
    <div role="alert" className="rounded border border-danger/40 bg-danger/50 p-3 text-sm text-danger">
      {children}
      {reason != null && reason !== '' && (
        <div className="mt-1 font-mono text-xs opacity-75">{reason}</div>
      )}
    </div>
  );
}

// ── Section ────────────────────────────────────────────────

/**
 * A card's content, at the same inset as its header.
 *
 * This used to be `<Section>` doing double duty — page-level
 * grouping *and* card body — which is why card content sat flush
 * against the border with a stray 32px gap under it: the grouping
 * component's `mb-8` was landing inside the card. One element, one
 * job.
 */
export function CardBody({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return <div className={`px-5 py-4 ${className}`}>{children}</div>;
}

/** A titled group of cards on a page. Not a card's interior — that
 *  is `CardBody`. */
export function Section({
  title,
  children,
  action,
}: {
  title?: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="mb-8">
      {(title || action) && (
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-[0.12em] text-fg-subtle">
            {title}
          </h3>
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

// ── Tabs ───────────────────────────────────────────────────

export function Tabs({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div className="flex gap-1 border-b border-border">
      {options.map((o) => (
        <Action
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`border-b-2 px-3 py-2 text-sm transition ${
            value === o.value
              ? 'border-accent text-fg'
              : 'border-transparent text-fg-muted hover:text-fg'
          }`}
        >
          {o.label}
        </Action>
      ))}
    </div>
  );
}

// ── Format helpers ─────────────────────────────────────────

/**
 * "8 minutes ago", in the reader's language.
 *
 * This used to append the English suffix by hand — `${n}m ago` — so a
 * Japanese dashboard still said "15m ago" beside 最終発生. Every
 * platform ships the plural rules and the word order for this;
 * `Intl.RelativeTimeFormat` is the whole answer, and the `narrow`
 * style keeps the string short enough for a table cell.
 *
 * The locale comes from the document rather than a prop because this
 * is called from ten files, most of them deep inside table cell
 * renderers where threading a locale down would mean touching every
 * column definition to fix a suffix.
 */
/** Bytes at a scale a person reads. A dSYM is the reason: a real
 *  iOS one runs to hundreds of megabytes, and the releases panel
 *  rendered insight-mobile's as `297713 KB` — a number nobody
 *  converts in their head, in the column whose whole job is telling
 *  an operator at a glance what landed. Binary units, because that
 *  is what every other tool reporting a build artifact uses. */
export function formatBytes(n: number | undefined): string {
  if (n === undefined || !Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  // One decimal below 10 so 1.4 MB and 9.8 MB stay distinguishable;
  // none above, where the digit is noise against the magnitude.
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/**
 * A moment, shown the way people read one and hoverable for the way
 * machines record one.
 *
 * Every timestamp in this dashboard was relative and only relative.
 * That reads well for "2 minutes ago" and falls apart for the audit
 * log, where the whole point is lining events up against something
 * that happened outside this product — and for a device whose clock is
 * wrong, where "2 months ago" is the phone's opinion, not ours.
 */
export function TimeAgo({
  iso,
  className,
}: {
  className?: string;
  iso: string | null | undefined;
}) {
  return (
    <time className={className} dateTime={iso ?? undefined} title={formatAbsolute(iso)}>
      {formatRelative(iso)}
    </time>
  );
}

/**
 * The absolute timestamp a relative one stands for, with its offset.
 *
 * `formatRelative` has said since it was written that "the exact
 * timestamp is in the title attribute wherever this is rendered". It
 * was in none of them: twenty-five call sites, zero titles. An audit
 * log whose every row reads "3 minutes ago" cannot do the one job an
 * audit log has.
 */
export function formatAbsolute(iso: string | null | undefined): string {
  const ms = iso == null ? NaN : new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const locale =
    typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en';
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'long',
  }).format(ms);
}

export function formatRelative(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  // A formatter handed no timestamp has nothing to format. Several
  // columns are genuinely nullable — `resolved_at` on an unresolved
  // issue, `next_attempt_at` on a send that will not be retried — and
  // `new Date(null).getTime()` is NaN. `Intl.RelativeTimeFormat`
  // *throws* on a non-finite number rather than printing one, so the
  // NaN that the old hand-rolled version rendered as a cosmetic
  // "NaNs ago" now takes the whole page down with it.
  const ms = iso == null ? NaN : new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const sec = Math.abs(now - ms) / 1000;
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    sec < 60
      ? [Math.max(1, Math.round(sec)), 'second']
      : sec < 3600
        ? [Math.round(sec / 60), 'minute']
        : sec < 86_400
          ? [Math.round(sec / 3600), 'hour']
          : sec < 86_400 * 30
            ? [Math.round(sec / 86_400), 'day']
            : sec < 86_400 * 365
              ? [Math.round(sec / 86_400 / 30), 'month']
              : [Math.round(sec / 86_400 / 365), 'year'];
  // A device with a broken clock produced a queue row reading
  // "7,979 years ago". The number is arithmetically right and tells
  // the reader nothing except that something is wrong somewhere else.
  // Past a decade, say that instead — the exact timestamp is in the
  // title attribute wherever this is rendered.
  if (unit === 'year' && value > 10) return '—';
  const locale =
    typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en';
  return new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'narrow' })
    .format(-value, unit);
}

export function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

export function clsx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/// Three-state theme switch — a segmented row that reads as
/// furniture, not as a feature. State + persistence live in
/// lib/theme.ts.
///
/// Here rather than in App.tsx because the docs pages are outside the
/// authenticated shell and need it too; importing it from App would
/// have pulled the whole dashboard into the chunk a stranger loads to
/// read a page.
/// furniture, not as a feature. State + persistence live in
/// lib/theme.ts.
export function ThemeSwitch() {
  const t = useT();
  const mode = useThemeMode();
  const setMode = setThemeMode;
  const options: {
    mode: ThemeMode;
    label: string;
    Glyph: typeof Monitor;
  }[] = [
    { mode: 'system', label: t('theme.system'), Glyph: Monitor },
    { mode: 'light', label: t('theme.light'), Glyph: Sun },
    { mode: 'dark', label: t('theme.dark'), Glyph: Moon },
  ];
  return (
    <div
      role="radiogroup"
      aria-label={t('theme.label')}
      className="theme-switch flex rounded-md border border-border p-0.5"
    >
      {options.map((o) => (
        <Action
          key={o.mode}
          type="button"
          role="radio"
          aria-checked={mode === o.mode}
          title={o.label}
          aria-label={o.label}
          onClick={() => setMode(o.mode)}
          className={`flex flex-1 items-center justify-center rounded py-1 transition-colors ${
            mode === o.mode
              ? 'bg-raised text-fg'
              : 'text-fg-subtle hover:text-fg-muted'
          }`}
        >
          <o.Glyph aria-hidden className="h-3.5 w-3.5" />
        </Action>
      ))}
    </div>
  );
}
